import { useState } from "react";
import {
  List,
  DataTable,
  ReferenceField,
  CreateButton,
} from "@/components/admin";
import { Badge } from "@/components/ui/badge";
import { useRecordContext, useListContext } from "ra-core";
import {
  Briefcase,
  DollarSign,
  Activity,
  Calendar,
  Hash,
  ListOrdered,
} from "lucide-react";
import type { Proposal } from "../../providers/fakerest/dataGenerator/types";
import { TopToolbar } from "../../layout/TopToolbar";
import {
  FilterSidebar,
  FilterButton,
  type FilterSection,
  type FilterOption,
} from "../../filters/FilterSidebar";

// Column header with icon helper
const ColHeader = ({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) => (
  <span className="flex items-center gap-1.5">
    <Icon className="h-4 w-4 text-muted-foreground" />
    {label}
  </span>
);

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  sent: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  accepted: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

const StatusBadge = (record: Proposal) => {
  const status = record.status;
  return (
    <Badge
      className={
        statusColors[status] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
      }
    >
      {status}
    </Badge>
  );
};

const LineItemsCount = (record: Proposal) => {
  const items = record.line_items;
  return <span>{items?.length || 0} items</span>;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const TotalCell = (record: Proposal) => (
  <span className="font-medium">{formatCurrency(record.total)}</span>
);

const JobAddress = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span className="truncate max-w-[200px]">{record.address}</span>;
};

// Filter configuration
const statusSection: FilterSection = {
  id: "status",
  label: "Status",
  icon: <Activity className="h-4 w-4" />,
  type: "multi",
  source: "status",
  options: [
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "accepted", label: "Accepted" },
    { value: "declined", label: "Declined" },
  ],
};

const filterSections: FilterSection[] = [statusSection];

const sortOptions: FilterOption[] = [
  { value: "created_at:DESC", label: "Newest first" },
  { value: "created_at:ASC", label: "Oldest first" },
  { value: "total:DESC", label: "Highest total" },
  { value: "total:ASC", label: "Lowest total" },
  { value: "valid_until:ASC", label: "Expiring soon" },
];

const ProposalListActions = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const { filterValues } = useListContext();

  const activeFilterCount = Object.entries(filterValues || {}).filter(
    ([key, value]) => key !== "q" && value !== undefined && value !== "",
  ).length;

  return (
    <>
      <TopToolbar>
        <FilterButton
          onClick={() => setFilterOpen(true)}
          activeCount={activeFilterCount}
        />
        <CreateButton />
      </TopToolbar>
      <FilterSidebar
        open={filterOpen}
        onOpenChange={setFilterOpen}
        sections={filterSections}
        sortOptions={sortOptions}
      />
    </>
  );
};

export const ProposalList = () => (
  <List actions={<ProposalListActions />} perPage={1000} pagination={false}>
    <DataTable<Proposal> rowClick="show">
      <DataTable.Col
        source="id"
        label={<ColHeader icon={Hash} label="ID" />}
        headerClassName="w-[60px]"
        cellClassName="w-[60px]"
      />
      <DataTable.Col
        label={<ColHeader icon={Briefcase} label="Job" />}
        headerClassName="w-[350px]"
        cellClassName="w-[350px]"
      >
        <ReferenceField source="job_id" reference="jobs" link={false}>
          <JobAddress />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col
        source="line_items"
        label={<ColHeader icon={ListOrdered} label="Items" />}
        render={LineItemsCount}
        headerClassName="w-[100px]"
        cellClassName="w-[100px]"
      />
      <DataTable.Col
        source="total"
        label={<ColHeader icon={DollarSign} label="Total" />}
        render={TotalCell}
        headerClassName="text-right w-[120px]"
        cellClassName="text-right w-[120px]"
      />
      <DataTable.Col
        source="status"
        label={<ColHeader icon={Activity} label="Status" />}
        render={StatusBadge}
        headerClassName="w-[100px]"
        cellClassName="w-[100px]"
      />
      <DataTable.Col
        source="valid_until"
        label={<ColHeader icon={Calendar} label="Valid Until" />}
        headerClassName="w-[120px]"
        cellClassName="w-[120px]"
      />
    </DataTable>
  </List>
);
